import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Crown, Zap, Briefcase, Sparkles, ArrowLeft, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";

const PLAN_VISUALS = {
  free: { Icon: Zap, color: "from-teal-soft to-teal", description: "Empieza gratis." },
  standard: { Icon: Crown, color: "from-orange to-amber-500", description: "Para emprendedores que crecen." },
  business: { Icon: Briefcase, color: "from-teal-deep to-teal", description: "Para negocios establecidos." },
  enterprise: { Icon: Sparkles, color: "from-violet-600 to-rose-500", description: "Plan a medida." },
};

const FEATURES = {
  free: [
    "Acceso a la plataforma",
    "Calendario comunitario",
    "Directorio",
    "Citas con profesionales",
    "Hasta 3 perfiles diferentes",
    "Hasta 3 productos/servicios",
    "Visibilidad limitada",
  ],
  standard: [
    "Todo lo del Plan Free",
    "Hasta 5 perfiles",
    "Hasta 10 productos/servicios",
    "Banners rotativos destacados",
    "1 campaña de mailing mensual",
    "Estadísticas avanzadas",
    "Mejor posicionamiento en búsquedas",
  ],
  business: [
    "Todo lo del Plan Estándar",
    "Todos los tipos de perfiles",
    "Hasta 20 productos/servicios",
    "Hasta 5 campañas de mailing al mes",
    "Promoción con popups",
    "Mayor prioridad en búsquedas",
    "Reportes avanzados",
    "Espacios promocionales premium",
  ],
  enterprise: [
    "Más de 20 productos/servicios",
    "Plan a medida",
    "Límites configurables",
    "Campañas especiales",
    "Patrocinios y posicionamiento destacado",
    "Atención personalizada",
  ],
};

const PlansPage = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {});
    if (user) {
      api.get("/me/plan").then(({ data }) => setCurrent(data)).catch(() => {});
    }
  }, [user]);

  const requestEnterprise = async () => {
    setRequesting(true); setFeedback("");
    try {
      await api.post("/me/plan/request-upgrade", { message: "Solicito plan Empresarial." });
      setFeedback(t.plans.requestSent);
    } catch (err) { setFeedback(formatApiError(err)); }
    finally { setRequesting(false); }
  };

  return (
    <section className="section bg-cream min-h-[80vh]">
      <div className="container-tight">
        {user && (
          <Link to="/my-profiles" className="text-sm text-teal-soft hover:text-orange inline-flex items-center gap-1" data-testid="back-link">
            <ArrowLeft size={14} /> {t.myProfiles.backToPanel}
          </Link>
        )}
        <div className="text-center mt-4">
          <p className="eyebrow text-orange">{t.plans.eyebrow}</p>
          <h1 className="font-display text-4xl md:text-5xl text-teal-deep leading-tight mt-2">{t.plans.title}</h1>
          <p className="text-teal-soft mt-3 max-w-2xl mx-auto">{t.plans.subtitle}</p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="plans-grid">
          {plans.map((p) => {
            const visual = PLAN_VISUALS[p.id] || PLAN_VISUALS.free;
            const Icon = visual.Icon;
            const isCurrent = current?.plan === p.id;
            const features = FEATURES[p.id] || [];
            return (
              <div
                key={p.id}
                className={`bg-white rounded-2xl border-2 p-6 flex flex-col ${isCurrent ? "border-orange shadow-xl" : "border-gray-200"}`}
                data-testid={`plan-card-${p.id}`}
              >
                <span className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${visual.color} text-white flex items-center justify-center`}>
                  <Icon size={20} />
                </span>
                <h3 className="font-display text-2xl text-teal-deep mt-4">{p.label}</h3>
                <p className="text-sm text-teal-soft mt-1">{visual.description}</p>
                <div className="mt-4 text-sm">
                  <div className="text-teal-deep font-bold">{p.profiles >= 99 ? (p.profiles === 999 ? "Perfiles ilimitados" : "Hasta +10 perfiles") : `${p.profiles} perfil(es)`}</div>
                  <div className="text-teal-soft">{p.products_per_profile === 999 ? "Productos/servicios sin límite" : `${p.products_per_profile} productos/servicios por perfil`}</div>
                </div>
                <ul className="mt-4 space-y-2 flex-1">
                  {features.map((f) => (
                    <li key={f} className="text-sm text-teal-deep inline-flex gap-2 items-start">
                      <Check size={14} className="text-orange mt-1 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5">
                  {isCurrent ? (
                    <span className="block text-center text-sm font-bold text-orange py-2 border-2 border-orange/30 rounded-full">{t.plans.currentPlan}</span>
                  ) : p.id === "enterprise" ? (
                    <button
                      type="button"
                      onClick={requestEnterprise}
                      disabled={requesting || !user}
                      className="btn-teal w-full justify-center disabled:opacity-50"
                      data-testid="request-enterprise-btn"
                    >
                      <Send size={14} /> {requesting ? t.plans.sending : t.plans.requestUpgrade}
                    </button>
                  ) : (
                    <p className="text-xs text-center text-teal-soft">{t.plans.contactAdmin}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {feedback && <p className="mt-6 text-center text-sm text-orange" data-testid="plans-feedback">{feedback}</p>}
      </div>
    </section>
  );
};

export default PlansPage;
